import * as XLSX from 'xlsx';
import { formatMoney } from '../components/groups/GroupListPayment';
/**
 * Utilidad genérica para construir reportes en Excel
 * Permite generar archivos Excel con formato consistente
 */

/**
 * Crea un archivo Excel con los datos transformados
 * @param {Array} transformedData - Array de objetos con los datos a exportar
 * @param {Array} headers - Array con los nombres de las columnas
 * @param {String} fileName - Nombre del archivo a descargar
 * @param {String} sheetName - Nombre de la hoja del Excel
 * @param {Object} config - Configuración adicional
 * @param {Array} config.moneyCols - Array con los nombres de columnas que tienen formato de moneda
 * @param {Array} config.sumCols - Array con referencias de columnas para sumar (ej: ['I', 'J'])
 * @param {Boolean} config.useDynamicRange - Si true, usa el rango dinámico para calcular última fila
 * @returns {void} Descarga el archivo Excel
 */
export const buildExcelReport = (
  transformedData,
  headers,
  fileName,
  sheetName,
  config = {}
) => {
  const {
    moneyCols = [],
    sumCols = [],
    useDynamicRange = false,
    onError = null,
  } = config;

  if (!transformedData || !transformedData.length) {
    return;
  }

  try {
    const dateStr = new Date().toISOString().slice(0, 10);
    const finalFileName = `${fileName} ${dateStr}.xlsx`;

    // Crear worksheet desde los datos
    const worksheet = XLSX.utils.json_to_sheet(transformedData);

    // Configurar ancho de columnas
    worksheet['!cols'] = headers.map((h) => {
      let max = String(h).length;
      for (let i = 0; i < transformedData.length; i++) {
        const cell = transformedData[i][h];
        const text =
          cell == null
            ? ''
            : moneyCols.includes(h) && typeof cell === 'number'
              ? formatMoney(cell)
              : String(cell);
        if (text.length > max) max = text.length;
      }
      return { wch: Math.min(Math.max(max + 2, 8), 50) };
    });

    // Aplicar formato de moneda a las columnas especificadas
    moneyCols.forEach((col) => {
      for (let row = 1; row <= transformedData.length + 1; row++) {
        const cellRef = `${col}${row}`;
        if (worksheet[cellRef]) worksheet[cellRef].z = '$#,##0.00';
      }
    });

    // Agregar encabezados (sobrescribir los generados automáticamente)
    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: 'A1' });

    // Calcular fila de totales
    let totalRow;
    if (useDynamicRange && worksheet['!ref']) {
      const range = XLSX.utils.decode_range(worksheet['!ref']);
      totalRow = range.e.r + 2; // +1 por encabezado, +1 porque Excel es 1-based
    } else {
      totalRow = transformedData.length + 2; // +1 por encabezado, +1 porque Excel es 1-based
    }

    // Agregar sumatorias en las columnas especificadas
    sumCols.forEach((col) => {
      worksheet[`${col}${totalRow}`] = {
        f: `SUM(${col}2:${col}${totalRow - 1})`,
        t: 'n',
        v: 0,
      };
    });

    // Actualizar rango si se usa dinámico
    if (useDynamicRange && worksheet['!ref']) {
      const range = XLSX.utils.decode_range(worksheet['!ref']);
      range.e.r = totalRow - 1;
      worksheet['!ref'] = XLSX.utils.encode_range(range);
    }

    // Crear y descargar workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(workbook, finalFileName);
  } catch (error) {
    console.error('Error al crear archivo Excel:', error);
    if (onError) {
      onError(error);
    } else {
      alert('Ha ocurrido un error al intentar descargar el archivo Excel');
    }
  }
};
